import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_COMPOSITE_COMPONENTS_QUERY_KEY } from '@/state/queries/composite-components/use-get-composite-components';
import type {
  CompositeComponentDto,
  CompositeRole,
} from '@/state/queries/composite-components/use-get-composite-components';

export type AllowedInContext = 'event' | 'flow' | 'break' | 'session';

export interface CreateCompositeComponentDto {
  name: string;
  slug: string;
  definition: {
    version: number;
    components: Array<
      | {
          type: 'html';
          ref: string;
          position: { x: number; y: number; w: number; h: number };
          parameters?: Record<string, string>;
          arrayPath?: string;
        }
      | { type: 'media'; ref: string; position: { x: number; y: number; w: number; h: number } }
    >;
  };
  scope?: 'global';
  role?: CompositeRole;
  allowedIn?: AllowedInContext[];
  applicability?: {
    eventTypes?: string[];
    flowScopes?: string[];
    sessionTypes?: string[];
    slotContentTypes?: string[];
  };
  sortOrderByScope?: Record<string, number>;
}

export const useCreateCompositeComponentMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCompositeComponentDto) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/composite-components`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: data,
        token: token ?? undefined,
      }) as Promise<CompositeComponentDto>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_COMPOSITE_COMPONENTS_QUERY_KEY] });
    },
  });
};
